import { useState, useEffect, useMemo } from 'react'
import Papa from 'papaparse'
import { detectCsvEncoding } from '../../utils/csvEncoding'
import { useParams, useNavigate } from 'react-router-dom'
import { useTeams } from '../../hooks/useTeams'
import { supabase } from '../../lib/supabase'
import { useTenant } from '../../contexts/TenantContext'
import { Button, Input, Modal, Badge, DarkModeToggleCompact } from '../../components/ui'
import { Users, Plus, Trash2, ArrowLeft, Edit2, Sparkles, Upload, Link as LinkIcon, AlertCircle, Download, FileUp, ChevronDown, ChevronUp, CheckCircle2, ArrowRight, Gamepad } from 'lucide-react'
import { usePoules } from '../../hooks/usePoules'
import { AITeamGenerator } from '../../components/tools/AITeamGenerator'
import { HelpTip } from '../../components/ui/HelpTip'
import { TeamAvatar } from '../../components/ui/TeamAvatar'
import { soundFx } from '../../lib/soundEffects'

import { usePlayers } from '../../hooks/usePlayers'

import { useTranslation } from 'react-i18next'

import { getTournamentLabels } from '../../utils/tournamentLabels'

export const TeamsPage = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { tournamentId } = useParams()
    const { activeTenant } = useTenant()
    const { teams, loading, createTeam, updateTeam, deleteTeam } = useTeams(tournamentId)
    // We gebruiken usePlayers hier algemeen, en straks specifiek per team in de modal
    const { createPlayer, deletePlayer, players: allPlayers } = usePlayers(tournamentId)
    const { poules } = usePoules(tournamentId)
    const [showModal, setShowModal] = useState(false)
    const [editingTeam, setEditingTeam] = useState(null)
    const [managingPlayersTeam, setManagingPlayersTeam] = useState(null) // New state for player modal
    const [newTeam, setNewTeam] = useState({ name: '', logo_url: '', poule_id: '' })
    const [uploading, setUploading] = useState(false)
    const [sportType, setSportType] = useState('goals')
    const [sportFormat, setSportFormat] = useState('teams')
    const [showNameGenerator, setShowNameGenerator] = useState(false)

    // Get dynamic labels based on sport type and format (with useMemo to prevent re-computation)
    const labels = useMemo(() => getTournamentLabels(sportType, sportFormat), [sportType, sportFormat]);

    useEffect(() => {
        supabase.from('tournaments')
            .select('sport_type, sport_format')
            .eq('id', tournamentId)
            .single()
            .then(({ data }) => {
                if (data) {
                    setSportType(data.sport_type || 'goals')
                    setSportFormat(data.sport_format || 'teams')
                }
            })
    }, [tournamentId])



    const handleCreate = async (e) => {
        if (e && e.preventDefault) e.preventDefault()
        try {
            const teamToCreate = { ...newTeam }
            const isPremium = activeTenant?.plan === 'pro' || activeTenant?.status === 'premium'
            if (!isPremium && teams.length >= 10) {
                soundFx.play('warning')
                alert(t('teams_page.limit_reached'))
                return
            }
            // Use selected poule_id from form, or auto-assign if only 1 poule exists
            if (!teamToCreate.poule_id && poules.length === 1) {
                teamToCreate.poule_id = poules[0].id
            }
            if (!teamToCreate.poule_id) delete teamToCreate.poule_id // Don't send empty string
            await createTeam(teamToCreate)
            soundFx.play('success')
            setShowModal(false)
            setNewTeam({ name: '', logo_url: '', poule_id: '' })
        } catch (err) {
            alert('Fout: ' + err.message)
        }
    }

    const handleAddGeneratedTeam = async (name) => {
        try {
            const isPremium = activeTenant?.plan === 'pro' || activeTenant?.status === 'premium'
            if (!isPremium && teams.length >= 10) {
                soundFx.play('warning')
                alert("⚠️ Gratis Plan Limiet Bereikt\n\nJe kunt maximaal 10 teams toevoegen in het gratis pakket.\nUpgrade naar PRO voor onbeperkte teams!")
                return
            }

            let pouleId = null
            if (poules.length === 1) pouleId = poules[0].id

            await createTeam({ name, poule_id: pouleId })

            // Optional: Feedback toast could go here
        } catch (err) {
            alert('Fout: ' + err.message)
        }
    }

    const handleUpdate = async (e) => {
        e.preventDefault()
        try {
            await updateTeam(editingTeam.id, {
                name: editingTeam.name,
                logo_url: editingTeam.logo_url,
                poule_id: editingTeam.poule_id
            })
            setEditingTeam(null)
        } catch (err) {
            alert('Fout: ' + err.message)
        }
    }

    const handleDelete = async (teamId) => {
        if (!confirm(t('teams_page.confirm_delete'))) return
        try {
            await deleteTeam(teamId)
        } catch (err) {
            alert('Fout: ' + err.message)
        }
    }



    const generateCustomLogo = (isEdit = false) => {
        const teamName = isEdit ? editingTeam?.name : newTeam.name
        if (!teamName) {
            alert(`Vul eerst een ${labels.entityLower}naam in!`)
            return
        }

        const canvas = document.createElement('canvas')
        canvas.width = 512
        canvas.height = 512
        const ctx = canvas.getContext('2d')

        // Professional gradients palette (Modern/Sports/Esports)
        const palettes = [
            ['#4F46E5', '#7C3AED'], // Indigo -> Purple
            ['#2563EB', '#3B82F6'], // Blue
            ['#059669', '#10B981'], // Emerald
            ['#DC2626', '#EF4444'], // Red
            ['#D97706', '#F59E0B'], // Amber
            ['#DB2777', '#EC4899'], // Pink
            ['#0891B2', '#06B6D4'], // Cyan
            ['#111827', '#374151'], // Dark Gray
            ['#7C3AED', '#DB2777']  // Purple -> Pink (Esports)
        ]

        // Hash name to pick consistent color
        let hash = 0
        for (let i = 0; i < teamName.length; i++) {
            hash = teamName.charCodeAt(i) + ((hash << 5) - hash)
        }
        const colorIndex = Math.abs(hash) % palettes.length
        const [color1, color2] = palettes[colorIndex]

        // Background Gradient
        const gradient = ctx.createLinearGradient(0, 0, 512, 512)
        gradient.addColorStop(0, color1)
        gradient.addColorStop(1, color2)
        ctx.fillStyle = gradient
        ctx.fillRect(0, 0, 512, 512)

        // Overlay pattern (tech lines for Esports)
        if (sportType === 'esports') {
            ctx.strokeStyle = 'rgba(255,255,255,0.1)'
            ctx.lineWidth = 2
            ctx.beginPath()
            for (let i = 0; i < 512; i += 40) {
                ctx.moveTo(i, 0); ctx.lineTo(i, 512);
                ctx.moveTo(0, i); ctx.lineTo(512, i);
            }
            ctx.stroke()
        }

        // Circular border (Shield-like)
        ctx.strokeStyle = 'rgba(255,255,255,0.2)'
        ctx.lineWidth = 20
        ctx.beginPath()
        ctx.arc(256, 256, 230, 0, Math.PI * 2)
        ctx.stroke()

        // Text (Initials)
        const acronym = teamName
            .replace(/[^a-zA-Z0-9 ]/g, '') // Remove special chars for initials
            .split(' ')
            .map(n => n[0])
            .slice(0, 2)
            .join('')
            .toUpperCase() || teamName.substring(0, 2).toUpperCase()

        // Text Shadow
        ctx.shadowColor = 'rgba(0,0,0,0.4)'
        ctx.shadowBlur = 20
        ctx.shadowOffsetX = 5
        ctx.shadowOffsetY = 10

        // Draw Text
        ctx.fillStyle = '#FFFFFF'
        ctx.font = 'bold 220px Inter, system-ui, sans-serif'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(acronym, 256, 256 + 15) // +15 for optical centering

        try {
            const dataUrl = canvas.toDataURL('image/png')
            if (isEdit) {
                setEditingTeam({ ...editingTeam, logo_url: dataUrl })
            } else {
                setNewTeam({ ...newTeam, logo_url: dataUrl })
            }
            soundFx.play('success')
        } catch (err) {
            console.error('Logo generation error:', err)
            alert('Kon geen logo genereren.')
        }
    }

    const handleLogoUpload = async (e, isEdit = false) => {
        const file = e.target.files[0]
        if (!file) return
        setUploading(true)
        try {
            const fileExt = file.name.split('.').pop()
            const fileName = `teams/${tournamentId}/${Date.now()}.${fileExt}`
            const { error: uploadError } = await supabase.storage.from('images').upload(fileName, file)
            if (uploadError) throw uploadError
            const { data: { publicUrl } } = supabase.storage.from('images').getPublicUrl(fileName)
            if (isEdit) {
                setEditingTeam(prev => ({ ...prev, logo_url: publicUrl }))
            } else {
                setNewTeam(prev => ({ ...prev, logo_url: publicUrl }))
            }
        } catch (err) {
            console.error('Upload Error:', err)
            alert('Logo upload mislukt: ' + err.message)
        } finally {
            setUploading(false)
        }
    }

    const downloadTemplate = () => {
        const headers = ['Naam', 'Poule', 'LogoURL']
        const csvContent = headers.join(',')
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.setAttribute('download', 'teams_import_template.csv')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    const copySignupLink = () => {
        const url = `${window.location.origin}/join/${tournamentId}`
        navigator.clipboard.writeText(url)
        alert(t('teams_page.signup_link_copied', { url }))
    }

    const downloadCSV = () => {
        if (!teams || teams.length === 0) {
            alert('Geen teams om te exporteren.')
            return
        }
        const headers = ['Naam', 'LogoURL']
        const csvContent = [
            headers.join(','),
            ...teams.map(t => `"${t.name}","${t.logo_url || ''}"`)
        ].join('\n')
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.setAttribute('download', `teams_export_${new Date().toISOString().split('T')[0]}.csv`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    }

    const handleFileUpload = async (event) => {
        const file = event.target.files[0]
        if (!file) return

        const encoding = await detectCsvEncoding(file)

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            encoding: encoding,
            complete: async (results) => {
                const data = results.data
                if (!data || data.length === 0) {
                    alert('Het bestand is leeg of kon niet worden gelezen.')
                    return
                }

                const isPremium = activeTenant?.plan === 'pro' || activeTenant?.status === 'premium'
                const newTeamsCount = data.length
                if (!isPremium && (teams.length + newTeamsCount) > 10) {
                    soundFx.play('error')
                    alert(`⚠️ Import Mislukt: Gratis Limiet\n\nJe probeert ${newTeamsCount} teams te importeren, maar je hebt er al ${teams.length}.\nHet maximum voor gratis accounts is 10 teams.\n\nUpgrade naar PRO voor onbeperkte teams!`)
                    return
                }

                setUploading(true)
                let successCount = 0
                let errors = []

                // Pre-fetch poules to map poule name to id
                const { data: existingPoules } = await supabase.from('poules').select('id, name').eq('tournament_id', tournamentId)
                let pouleMap = {}
                if (existingPoules) {
                    existingPoules.forEach(p => pouleMap[p.name.toLowerCase()] = p.id)
                }

                for (let i = 0; i < data.length; i++) {
                    const row = data[i]
                    // Dynamically map columns in case of capitalization differences
                    const nameKey = Object.keys(row).find(k => k.toLowerCase() === 'naam' || k.toLowerCase() === 'name' || k.toLowerCase() === 'team')
                    const pouleKey = Object.keys(row).find(k => k.toLowerCase() === 'poule' || k.toLowerCase() === 'groep' || k.toLowerCase() === 'group')
                    const logoKey = Object.keys(row).find(k => k.toLowerCase() === 'logourl' || k.toLowerCase() === 'logo' || k.toLowerCase() === 'logo_url')

                    const name = nameKey ? row[nameKey]?.trim() : null
                    const pouleName = pouleKey ? row[pouleKey]?.trim() : null
                    const logoUrl = logoKey ? row[logoKey]?.trim() : ''

                    if (name) {
                        let pouleId = null
                        if (pouleName && pouleName.trim() !== '') {
                            const normPouleName = pouleName.toLowerCase()
                            if (pouleMap[normPouleName]) {
                                pouleId = pouleMap[normPouleName]
                            } else {
                                try {
                                    const { data: newPoule, error: pouleError } = await supabase.from('poules').insert({
                                        tournament_id: tournamentId,
                                        tenant_id: activeTenant?.id,
                                        name: pouleName
                                    }).select().single()
                                    if (pouleError) throw pouleError
                                    pouleId = newPoule.id
                                    pouleMap[normPouleName] = newPoule.id
                                } catch (err) {
                                    console.error("Error creating poule:", err)
                                    errors.push(`Regel ${i + 2}: Kon poule '${pouleName}' niet aanmaken.`)
                                }
                            }
                        }

                        try {
                            await createTeam({ name, logo_url: logoUrl, poule_id: pouleId })
                            successCount++
                        } catch (err) {
                            console.error(`Error importing line ${i + 2}:`, err)
                            errors.push(`Regel ${i + 2}: ${name} - ${err.message}`)
                        }
                    } else {
                        errors.push(`Regel ${i + 2}: Geen teamnaam gevonden.`)
                    }
                }

                setUploading(true) // Set back to false when fully complete
                setUploading(false)
                alert(t('teams_page.import_success', { count: successCount }) + (errors.length > 0 ? `\n\n${t('common.errors')}:\n${errors.join('\n')}` : ''))
                if (successCount > 0) window.location.reload()
                event.target.value = ''
            },
            error: (err) => {
                alert('Fout tijdens het parsen van CSV: ' + err.message)
            }
        })
    }

    const handleDeleteAll = async () => {
        if (teams.length === 0) return
        if (!confirm(t('teams_page.confirm_delete_all'))) return
        const check = prompt(t('teams_page.delete_all_prompt'))
        if (check !== 'VERWIJDEREN' && check !== 'DELETE' && check !== 'ELIMINAR') return // Allow multilingual confirmation keywords or just accept any
        // Ideally we check against the translated word, but for safety let's just stick to English generic or loosen it. 
        // For strictness, let's keep it simple:

        try {
            const { error } = await supabase.from('teams').delete().eq('tournament_id', tournamentId)
            if (error) throw error
            alert(t('teams_page.all_deleted'))
            window.location.reload()
        } catch (err) {
            alert('Fout: ' + err.message)
        }
    }

    const unassignedTeams = teams.filter(t => !t.poule_id)

    const handleAssignUnassigned = async (pouleId) => {
        if (!pouleId) return
        try {
            const { error } = await supabase.from('teams').update({ poule_id: pouleId }).in('id', unassignedTeams.map(t => t.id))
            if (error) throw error
            window.location.reload()
        } catch (err) {
            alert('Fout: ' + err.message)
        }
    }

    return (
        <div className="min-h-screen">
            <div className="premium-container">
                {/* Warning for unassigned teams */}
                {unassignedTeams.length > 0 && (
                    <div className="mb-8 glass-strong border-2 border-red-500 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4 animate-slideUp bg-red-50/50">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-red-100 text-red-600 rounded-xl">
                                <AlertCircle size={28} />
                            </div>
                            <div>
                                <h3 className="font-bold text-orange-800 text-lg">{t('teams_page.unassigned_warning', { count: unassignedTeams.length })}</h3>
                                <p className="text-sm text-orange-600">{t('teams_page.unassigned_desc')}</p>
                                {poules.length === 0 && <p className="text-sm text-red-600 font-bold mt-1">Er zijn nog GEEN poules aangemaakt! Ga naar 'Divisies & Poules'.</p>}
                            </div>
                        </div>
                        {poules.length > 0 ? (
                            <select
                                className="px-4 py-3 rounded-xl border-2 border-white/30 glass-subtle outline-none focus:border-[var(--accent-primary)] cursor-pointer"
                                onChange={(e) => handleAssignUnassigned(e.target.value)}
                                defaultValue=""
                            >
                                <option value="" disabled>{t('teams_page.select_poule_placeholder')}</option>
                                {poules.map(p => (
                                    <option key={p.id} value={p.id}>{t('teams_page.assign_all_to', { name: p.name })}</option>
                                ))}
                            </select>
                        ) : (
                            <Button onClick={() => navigate(`/admin/tournaments/${tournamentId}/divisions`)} variant="primary">
                                {t('teams_page.create_poule_btn')}
                            </Button>
                        )}
                    </div>
                )}

                {/* Hero Section */}
                <div className="mb-12 animate-slideUp">
                    <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="mb-4">
                        <ArrowLeft size={16} className="mr-2" />
                        {t('teams_page.back_dashboard')}
                    </Button>

                    <div className="flex justify-between items-start mb-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 rounded-2xl bg-gradient-to-br from-green-400 to-green-600 shadow-lg">
                                <Users className="text-white" size={32} />
                            </div>
                            <div>
                                <h1 className="text-5xl font-bold text-gray-900 leading-tight">{labels.entityPlural}</h1>
                                <p className="text-lg text-gray-600 mt-1">
                                    {labels.manage}
                                    {!(activeTenant?.plan === 'pro' || activeTenant?.status === 'premium') && (
                                        <Badge variant="default" size="sm" className="ml-2">
                                            {teams.length} / 10 (Gratis)
                                        </Badge>
                                    )}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <DarkModeToggleCompact />
                            <Button onClick={copySignupLink} variant="outline" icon={<LinkIcon size={18} />}>
                                Inschrijfformulier
                            </Button>
                        </div>
                    </div>



                    {/* Action Bar */}
                    <div className="flex flex-wrap gap-3 items-center glass-strong p-4 rounded-2xl border border-white/30">
                        <div className="flex gap-2 pr-3 border-r border-white/30">
                            {teams.length > 0 && (
                                <Button variant="ghost" onClick={handleDeleteAll} size="sm" className="text-red-500 hover:bg-red-50">
                                    <Trash2 size={16} />
                                </Button>
                            )}
                            <Button variant="ghost" onClick={downloadTemplate} size="sm" icon={<Download size={16} />}>
                                Template
                            </Button>
                            <label className="cursor-pointer">
                                <input type="file" accept=".csv" onChange={handleFileUpload} className="hidden" />
                                <div className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-white/20 rounded-lg transition-all">
                                    <FileUp size={16} className="mr-2" />
                                    Import
                                </div>
                            </label>
                            <Button variant="ghost" onClick={downloadCSV} size="sm" icon={<Upload size={16} className="rotate-180" />}>
                                Export
                            </Button>
                        </div>

                        <div className="flex-1"></div>

                        {/* AI Toggle Button */}
                        {/* AI Toggle Button */}
                        <Button
                            variant="secondary"
                            onClick={() => setShowNameGenerator(!showNameGenerator)}
                            className={`${showNameGenerator ? 'bg-indigo-100 text-indigo-700' : ''}`}
                            icon={<Sparkles size={16} />}
                        >
                            {t('teams_page.ai_inspiration_btn')} {showNameGenerator ? <ChevronUp size={16} className="ml-1" /> : <ChevronDown size={16} className="ml-1" />}
                        </Button>

                        <Button onClick={() => setShowModal(true)} variant="primary" icon={<Plus size={20} />}>
                            {labels.addNew}
                        </Button>
                    </div>

                    <div className="mt-6 mb-8">
                        <HelpTip title="💡 Tips voor het toevoegen">
                            {labels.importTip}<br />
                            {labels.aiTip}<br />
                            {labels.selfRegisterTip}
                        </HelpTip>
                    </div>
                </div>

                {/* AI Team Name Generator */}
                {showNameGenerator && (
                    <div className="mb-8 animate-slideUp">
                        <AITeamGenerator onAddTeam={handleAddGeneratedTeam} />
                    </div>
                )}

                {/* Teams Grid */}
                <div className="glass-strong premium-card rounded-2xl">
                    {loading || uploading ? (
                        <div className="flex flex-col items-center justify-center py-16">
                            <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-[var(--accent-primary)] mb-4"></div>
                            {uploading && <p className="text-gray-500 animate-pulse">{t('teams_page.uploading')}</p>}
                        </div>
                    ) : teams.length === 0 ? (
                        <div className="text-center py-20">
                            <div className="p-4 bg-gray-100 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                                <Users className="text-gray-400" size={40} />
                            </div>
                            <p className="text-gray-900 text-xl font-semibold mb-2">{labels.noEntities}</p>
                            <p className="text-gray-500">Klik op "{labels.addNew}" om te beginnen</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {teams.map(team => (
                                <div key={team.id} className="group glass-subtle rounded-xl p-6 border border-white/30 hover:scale-[1.02] transition-all">
                                    <div className="flex flex-col items-center text-center">
                                        <div className="mb-4">
                                            <TeamAvatar name={team.name} logoUrl={team.logo_url} size="lg" isGamer={sportType === 'esports'} />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-2">{team.name}</h3>
                                        {team.poule_id && poules.find(p => p.id === team.poule_id) && (
                                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full mb-2">
                                                {poules.find(p => p.id === team.poule_id).name}
                                            </span>
                                        )}
                                        <div className="flex flex-wrap justify-center gap-2 mt-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                            {/* Only show Players/Squad button if it's NOT a solo tournament */}
                                            {sportFormat !== 'individual' && sportType !== 'sets' && (
                                                <Button variant="secondary" onClick={() => setManagingPlayersTeam(team)} size="sm" icon={<Users size={14} />}>
                                                    Spelers
                                                </Button>
                                            )}
                                            {/* For Sets (Tennis/Padel) usually we just edit the names in the team object itself for now, or we could enable players there too, but let's keep it simple */}

                                            <Button variant="secondary" onClick={() => setEditingTeam(team)} size="sm" icon={<Edit2 size={14} />}>
                                                Bewerk
                                            </Button>
                                            <Button variant="ghost" onClick={() => handleDelete(team.id)} size="sm">
                                                <Trash2 size={14} className="text-red-500" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Navigation Action Bar */}
            {teams.length > 0 && (() => {
                // Issue #5: Navigate to poule indeling if teams are unassigned with multiple poules
                const hasUnassigned = unassignedTeams.length > 0 && poules.length > 1
                const nextPath = hasUnassigned
                    ? `/admin/tournaments/${tournamentId}/divisions`
                    : `/admin/tournaments/${tournamentId}/matches`
                const nextLabel = hasUnassigned
                    ? 'Volgende Stap: Teams Indelen'
                    : 'Volgende Stap: Schema'
                const subText = hasUnassigned
                    ? `⚠️ ${unassignedTeams.length} team(s) zijn nog niet aan een poule toegewezen.`
                    : 'Geweldig! Tijd om de wedstrijden te genereren.'

                return (
                    <div className="premium-container mt-8 pb-12">
                        <div className="glass-strong rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4 border border-[var(--accent-primary)]/20 shadow-lg animate-slideUp">
                            <div className="flex items-center gap-4">
                                <div className={`p-3 rounded-full ${hasUnassigned ? 'bg-orange-100' : 'bg-green-100'}`}>
                                    <CheckCircle2 className={hasUnassigned ? 'text-orange-500' : 'text-green-600'} size={24} />
                                </div>
                                <div>
                                    <h3 className="font-bold text-gray-900 text-lg">Zijn alle teams ingeschreven?</h3>
                                    <p className="text-gray-600 text-sm">{subText}</p>
                                </div>
                            </div>

                            <Button
                                variant="primary"
                                size="lg"
                                onClick={() => navigate(nextPath)}
                                className="w-full md:w-auto min-w-[200px] shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1"
                                icon={<ArrowRight size={20} />}
                            >
                                {nextLabel}
                            </Button>
                        </div>
                    </div>
                )
            })()}

            {/* Create Modal */}
            <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={t('teams_page.modal_create_title')} size="lg">
                <form onSubmit={handleCreate} className="space-y-6">
                    <Input
                        label={t('teams_page.input_name_label')}
                        value={newTeam.name}
                        onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                        placeholder={
                            sportType === 'sets' ? "bijv. Roger Federer (Enkel) of Janssen/De Boer (Dubbel)" :
                                sportFormat === 'individual' ? "bijv. xSlayer_99" :
                                    sportType === 'esports' ? "bijv. FaZe Clan" :
                                        "bijv. FC Twente"
                        }
                        icon={sportType === 'esports' ? <Gamepad size={18} /> : <Users size={18} />}
                        required
                    />
                    {sportType === 'sets' && <p className="text-xs text-gray-500 -mt-4">💡 Tip: Voor dubbelspel vul je beide namen in, gescheiden door een / of &.</p>}
                    {sportFormat === 'individual' && <p className="text-xs text-gray-500 -mt-4">💡 Tip: Vul hier de Gamertag in.</p>}
                    {sportFormat !== 'individual' && sportType === 'esports' && <p className="text-xs text-gray-500 -mt-4">💡 Tip: Vul hier de Team/Clan naam in. Spelers voeg je later toe.</p>}

                    <div>
                        <label className="label-premium mb-3 block">Team Logo (optioneel)</label>
                        {newTeam.logo_url && (
                            <div className="mb-4 flex justify-center">
                                <img src={newTeam.logo_url} alt="Preview" className="w-32 h-32 rounded-xl object-cover shadow-lg" />
                            </div>
                        )}
                        <div className="flex gap-3 mb-3">
                            <Button type="button" variant="secondary" onClick={() => generateCustomLogo(false)} className="flex-1" icon={<Sparkles size={16} />}>
                                ⚡ Auto Logo
                            </Button>
                            <label className={`flex-1 flex items-center justify-center px-4 py-2 glass-subtle border border-white/30 rounded-xl text-sm font-medium cursor-pointer hover:scale-105 transition-all ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                                {uploading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700 mr-2"></div> : <Upload size={16} className="mr-2" />}
                                {uploading ? 'Bezig...' : 'Uploaden'}
                                <input type="file" className="hidden" accept="image/*" onChange={(e) => handleLogoUpload(e, false)} />
                            </label>
                        </div>
                        <Input
                            placeholder="Of plak een afbeeldings-URL..."
                            value={newTeam.logo_url}
                            onChange={(e) => setNewTeam({ ...newTeam, logo_url: e.target.value })}
                        />
                        <p className="text-xs text-gray-500 mt-2">Gebruik de AI Generator, upload een bestand, of plak een URL.</p>
                    </div>

                    {/* Poule Assignment - only show when multiple poules exist */}
                    {poules.length > 1 && (
                        <div>
                            <label className="label-premium mb-3 block">{t('teams_page.input_poule_label')} <span className="text-gray-400 font-normal">(optioneel)</span></label>
                            <select
                                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[var(--accent-primary)] focus:ring-2 focus:ring-[var(--accent-primary)]/20 outline-none transition-all bg-white"
                                value={newTeam.poule_id}
                                onChange={(e) => setNewTeam({ ...newTeam, poule_id: e.target.value })}
                            >
                                <option value="">-- Kies een poule --</option>
                                {poules.map(p => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    <Button type="submit" variant="primary" size="lg" className="w-full" icon={<Plus size={20} />}>
                        {labels.entity} Aanmaken
                    </Button>
                </form>
            </Modal>

            {/* Edit Modal */}
            <Modal isOpen={!!editingTeam} onClose={() => setEditingTeam(null)} title={t('teams_page.modal_edit_title')} size="lg">
                <form onSubmit={handleUpdate} className="space-y-6">
                    <Input
                        label={t('teams_page.input_name_label')}
                        value={editingTeam?.name || ''}
                        onChange={(e) => setEditingTeam({ ...editingTeam, name: e.target.value })}
                        placeholder="bijv. FC Twente"
                        icon={<Users size={18} />}
                        required
                    />

                    <div>
                        <label className="label-premium mb-3 block">{t('teams_page.input_poule_label')}</label>
                        <select
                            className="w-full px-4 py-3 rounded-xl border-2 border-white/30 glass-subtle focus:border-[var(--accent-primary)] focus:ring-2 focus:ring-[var(--accent-primary)]/20 outline-none transition-all"
                            value={editingTeam?.poule_id || ''}
                            onChange={(e) => setEditingTeam({ ...editingTeam, poule_id: e.target.value })}
                        >
                            <option value="">{t('teams_page.no_poule_option')}</option>
                            {poules.map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="label-premium mb-3 block">Team Logo (optioneel)</label>
                        {editingTeam?.logo_url && (
                            <div className="mb-4 flex justify-center">
                                <img src={editingTeam.logo_url} alt="Preview" className="w-32 h-32 rounded-xl object-cover shadow-lg" />
                            </div>
                        )}
                        <div className="flex gap-3 mb-3">
                            <Button type="button" variant="secondary" onClick={() => generateCustomLogo(true)} className="flex-1" icon={<Sparkles size={16} />}>
                                ⚡ Auto Logo
                            </Button>
                            <label className={`flex-1 flex items-center justify-center px-4 py-2 glass-subtle border border-white/30 rounded-xl text-sm font-medium cursor-pointer hover:scale-105 transition-all ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                                {uploading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700 mr-2"></div> : <Upload size={16} className="mr-2" />}
                                {uploading ? 'Bezig...' : 'Uploaden'}
                                <input type="file" className="hidden" accept="image/*" onChange={(e) => handleLogoUpload(e, true)} />
                            </label>
                        </div>
                        <Input
                            placeholder="Of plak een afbeeldings-URL..."
                            value={editingTeam?.logo_url || ''}
                            onChange={(e) => setEditingTeam({ ...editingTeam, logo_url: e.target.value })}
                        />
                    </div>

                    <div className="flex gap-3">
                        <Button type="submit" variant="primary" className="flex-1">{t('common.save')}</Button>
                        <Button type="button" variant="ghost" onClick={() => setEditingTeam(null)} className="flex-1">{t('common.cancel')}</Button>
                    </div>
                </form>
            </Modal>

            {/* Player Manager Modal */}
            {managingPlayersTeam && (
                <PlayerManagerModal
                    team={managingPlayersTeam}
                    allPlayers={allPlayers}
                    onClose={() => setManagingPlayersTeam(null)}
                    onCreatePlayer={createPlayer}
                    onDeletePlayer={deletePlayer}
                    sportType={sportType}
                />
            )}
        </div>
    )
}



const PlayerManagerModal = ({ team, allPlayers, onClose, onCreatePlayer, onDeletePlayer, sportType }) => {
    const [name, setName] = useState('')
    const teamPlayers = allPlayers.filter(p => p.team_id === team.id)

    const handleAdd = async (e) => {
        e.preventDefault()
        if (!name.trim()) return
        try {
            await onCreatePlayer({ name, team_id: team.id })
            soundFx.play('select')
            setName('')
        } catch (err) {
            alert('Fout: ' + err.message)
        }
    }

    const handleDelete = async (id) => {
        if (!confirm('Speler verwijderen?')) return
        await onDeletePlayer(id)
    }

    return (
        <Modal isOpen={true} onClose={onClose} title={`${sportType === 'esports' ? 'Gamers' : 'Spelers'} van ${team.name}`} size="md">
            <div className="space-y-6">
                <form onSubmit={handleAdd} className="flex gap-2">
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={sportType === 'esports' ? "Gamertag (bijv. xKiller)" : "Naam speler"}
                        className="flex-1"
                        autoFocus
                    />
                    <Button type="submit" variant="primary" icon={<Plus size={18} />}>
                        Toevoegen
                    </Button>
                </form>

                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {teamPlayers.length === 0 && (
                        <p className="text-center text-gray-500 py-4 italic">Nog geen spelers toegevoegd.</p>
                    )}
                    {teamPlayers.map(player => (
                        <div key={player.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg group hover:bg-white hover:shadow-sm border border-transparent hover:border-gray-200 transition-all">
                            <span className="font-medium text-gray-800">{player.name}</span>
                            <button
                                onClick={() => handleDelete(player.id)}
                                className="text-gray-400 hover:text-red-500 p-1 opacity-0 group-hover:opacity-100 transition-all"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </Modal>
    )
}


